import { createRoot } from 'react-dom/client'
import { WebPlay } from '@playbooks/web-play-component'

import { init as initAnalytics, track } from './services/analytics'
import { init as initLocalization } from './services/localization'
import fomService from './services/fomService'
import addWebPlayCustomCss from './utils/addWebPlayCustomCss'
import getWebPlayInfoFromParentPage from './utils/getWebPlayInfoFromParentPage'
import getQueryParam from './utils/getQueryParam'

import './main.css'
import './i18n'

const urlParams = new URLSearchParams(window.location.search)

const playbookId = getQueryParam('playbookid', urlParams)
const showScore = getQueryParam('showscore', urlParams) !== null
const email = getQueryParam('email', urlParams)
const theme = getQueryParam('theme', urlParams)

const config = {
  serviceBaseUrl: import.meta.env.VITE_MEETING_SERVICE_URL,
  playbooksBackendUrl: import.meta.env.VITE_PLAYBOOKS_BACKEND_URL,
}

const submissionService = {
  createPlay: fomService.createPlay.bind(fomService),
  attachFileToPlay: fomService.attachFileToPlay.bind(fomService),
  attachActionCardToPlay: fomService.attachActionCardToPlay.bind(fomService),
  sendPlayCreatedNotification: fomService.sendPlayCreatedNotification.bind(fomService),
  rollbackPlayCreation: fomService.rollbackPlayCreation.bind(fomService),
  createAnonymousToken: fomService.createAnonymousToken.bind(fomService),
}

window.addEventListener('message', event => {
  // Only accept settings from the direct parent when running inside an iframe.
  // This is to prevent accepting messages from other sources which could be a security risk.
  if (window.self === window.top) return
  if (event.source !== window.parent) return
  if (event.data?.key === 'webPlayInfo') window.webPlayInfo = event.data.value
})

document.documentElement.classList.toggle(
  'dark',
  theme === 'dark' || (!theme && window.matchMedia('(prefers-color-scheme: dark)').matches),
)

const isDarkTheme = document.documentElement.classList.contains('dark')

;(async () => {
  try {
    let settings

    const { statusCode, webPlayInfo } = await fomService.getWebPlayInfo(playbookId!)
    // If the request to get web play info fails, we attempt to get it from the parent page. (window.webPlayInfo)
    if (statusCode === 200) {
      settings = webPlayInfo
    } else {
      const webPlayInfo = await getWebPlayInfoFromParentPage()
      settings = webPlayInfo
    }

    if (!settings) {
      document.getElementById('app')!.innerHTML =
        `<div class="text-red-600 p-4 text-center">Error: Unable to load web play settings.</div>`
      return
    }

    settings.privacyUrl ||= import.meta.env.VITE_PRIVACY_POLICY_URL

    if (statusCode !== 200 && !settings) {
      document.getElementById('app')!.innerHTML = `<div class="text-red-600 p-4 text-center">Error: ${statusCode}</div>`
      return
    }

    if (settings.weglotApiKey) {
      initLocalization(settings.weglotApiKey)
    }

    initAnalytics(settings, urlParams)

    const actionCard = await fomService.getActionCard({
      email,
      headersCount: settings.headersCount,
      actionCardUrl: settings.actionCardUrl,
      playbookId,
    })

    addWebPlayCustomCss(settings)

    createRoot(document.getElementById('app')!).render(
      <WebPlay
        actionCard={actionCard}
        settings={{ ...settings, isDarkTheme }}
        submissionService={submissionService}
        config={config}
        playbookId={playbookId!}
        showScore={showScore}
        trackFn={track}
      />,
    )
  } catch (error) {
    console.error('Failed to initialize the app:', error)
  }
})()
